import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import ToastMsg from "../../components/common/ToastMsg";
import { CUSTOM_CONFIGURATION, BRANCH } from "../../constants/api";
import { persistCustomConfig } from "../../utils/customConfigSession";
import {
    isOrgAdminUser,
    getActiveBranchId,
    getSessionBranchId,
    getSessionBranchName,
    isOrgOverview,
    BRANCH_FILTER_CHANGED_EVENT,
} from "../../utils/branchSession";
import useApiMethods from "../../lib/useApiMethods";
import useBranchRole from "../../hooks/useBranchRole";
import { SESSION_STORAGE_HYDRATED_EVENT } from "../../hooks/use-navigation";
import { Button } from "../../components/ui/button";
import { Save, Cog, Calculator, Percent, MapPin, Wallet, FileText, Clock, Receipt, Sparkles, Info, Cake, Building2, CalendarClock } from "lucide-react";
import { cn } from "../../utils/tailwindUtils";
import {
    SettingCard,
    ToggleRow,
    NumberField,
    SelectField,
    CompactSelect,
    PENALTY_MODE_OPTIONS,
    LinkCard,
    SectionHeader,
    ConfigBranchSelect,
} from "./configuration/settingUi";
import {
    CONFIG_SECTIONS,
    OPERATIONAL_LINKS,
    GRACE_TIME_OPTIONS,
    OVERTIME_ROUNDING_OPTIONS,
    TDS_REGIME_OPTIONS,
    DEFAULT_FORM_DATA,
    mapConfigToForm,
    BRANCH_SCOPED_SECTIONS,
    COMPANY_WIDE_FORM_FIELDS,
    BRANCH_SCOPED_FORM_FIELDS,
    pickFormFields,
    validateLateFineSlabs,
    validateAttendanceGrace,
} from "./configuration/configConstants";

const SettingsConfiguration = () => {
    const { fetch, update } = useApiMethods();
    const { isBranchManager } = useBranchRole();
    const isOrgAdmin = isOrgAdminUser();
    const ownBranchId = getSessionBranchId();
    const ownBranchName = getSessionBranchName();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [companyConfigData, setCompanyConfigData] = useState(null);
    const [branchConfigData, setBranchConfigData] = useState(null);
    const [branches, setBranches] = useState([]);
    const [selectedBranchId, setSelectedBranchId] = useState(null);
    const [activeSection, setActiveSection] = useState("overview");
    const [formData, setFormData] = useState({ ...DEFAULT_FORM_DATA });
    const [branchSelectionNotice, setBranchSelectionNotice] = useState(null);
    const [, setBranchScopeTick] = useState(0);

    const visibleSections = useMemo(() => {
        if (!isOrgAdmin) {
            return CONFIG_SECTIONS.filter((section) => section.id !== "features");
        }
        return CONFIG_SECTIONS;
    }, [isOrgAdmin]);

    const isBranchScopedSection = BRANCH_SCOPED_SECTIONS.has(activeSection);
    const showBranchPicker =
        isOrgAdmin &&
        isOrgOverview() &&
        isBranchScopedSection &&
        branches.length > 0;

    const activeMeta = useMemo(
        () => CONFIG_SECTIONS.find((s) => s.id === activeSection) || CONFIG_SECTIONS[0],
        [activeSection]
    );

    const applyCompanyFields = (data) => {
        const mapped = mapConfigToForm(data);
        setFormData((prev) => ({ ...prev, ...pickFormFields(mapped, COMPANY_WIDE_FORM_FIELDS) }));
    };

    const applyBranchFields = (data) => {
        const mapped = mapConfigToForm(data);
        setFormData((prev) => ({ ...prev, ...pickFormFields(mapped, BRANCH_SCOPED_FORM_FIELDS) }));
    };

    const fetchCompanyConfig = async () => {
        const response = await fetch(CUSTOM_CONFIGURATION, { company_wide: true });
        if (response.status === "success" && response.data?.[0]) {
            const data = response.data[0];
            setCompanyConfigData(data);
            applyCompanyFields(data);
            persistCustomConfig(data);
            return data;
        }
        return null;
    };

    const fetchBranchConfig = async (branchId) => {
        if (!branchId) return null;
        const response = await fetch(CUSTOM_CONFIGURATION, { branch: branchId });
        if (response.status === "success" && response.data?.[0]) {
            const data = response.data[0];
            setBranchConfigData(data);
            if (BRANCH_SCOPED_SECTIONS.has(activeSection)) {
                applyBranchFields(data);
            }
            return data;
        }
        return null;
    };

    const loadBranches = async () => {
        const orgAdmin = isOrgAdminUser();
        if (!orgAdmin) {
            const branchId = getSessionBranchId();
            if (isBranchManager && branchId) {
                setSelectedBranchId(String(branchId));
            }
            return branchId;
        }

        const response = await fetch(BRANCH, { drop_down: "True" });
        if (response.status !== "success") {
            setBranches([]);
            return null;
        }
        const raw = response?.data;
        const list = Array.isArray(raw)
            ? raw
            : (raw?.result || raw?.data || []);
        const activeBranches = Array.isArray(list)
            ? list.filter((branch) => branch.is_active !== false)
            : [];
        setBranches(activeBranches);

        const mainBranch = activeBranches.find((branch) => branch.is_main_branch) || activeBranches[0];
        let defaultId = mainBranch ? String(mainBranch.id) : null;

        // Overview header filter or direct branch view: default to the active branch.
        const activeBranchFilter = getActiveBranchId();
        if (
            activeBranchFilter &&
            activeBranchFilter !== "all" &&
            activeBranches.some((branch) => String(branch.id) === String(activeBranchFilter))
        ) {
            defaultId = String(activeBranchFilter);
        }

        setSelectedBranchId(defaultId);
        return defaultId;
    };

    const initializeConfiguration = async () => {
        setLoading(true);
        try {
            const company = await fetchCompanyConfig();
            const defaultBranchId = await loadBranches();
            if (defaultBranchId) {
                await fetchBranchConfig(defaultBranchId);
            } else if (company) {
                applyBranchFields(company);
                setBranchConfigData(company);
            }
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleBranchChange = async (branchId) => {
        setSelectedBranchId(branchId);
        setLoading(true);
        try {
            await fetchBranchConfig(branchId);
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const slabError = validateLateFineSlabs(formData.late_fine_slabs);
            if (slabError) {
                ToastMsg("error", slabError);
                return;
            }
            const graceError = validateAttendanceGrace(formData);
            if (graceError) {
                ToastMsg("error", graceError);
                return;
            }
            if (activeSection === "features") {
                if (!isOrgAdmin) {
                    ToastMsg("error", "Only organization admins can update Features & Access");
                    return;
                }
                const payload = {
                    ...pickFormFields(formData, COMPANY_WIDE_FORM_FIELDS),
                    id: companyConfigData?.id,
                    company_wide: true,
                };
                const response = await update(`${CUSTOM_CONFIGURATION}?company_wide=true`, payload);
                if (response.status === "success") {
                    ToastMsg("success", "Configuration saved successfully");
                    const updated = { ...(companyConfigData || {}), ...payload };
                    setCompanyConfigData(updated);
                    persistCustomConfig(updated);
                }
                return;
            }

            if (!selectedBranchId) {
                if (!branches.length) {
                    const payload = {
                        ...pickFormFields(formData, BRANCH_SCOPED_FORM_FIELDS),
                        id: companyConfigData?.id || branchConfigData?.id,
                    };
                    const response = await update(`${CUSTOM_CONFIGURATION}?company_wide=false`, payload);
                    if (response.status === "success") {
                        ToastMsg("success", "Configuration saved successfully");
                        const saved = response.data || payload;
                        setBranchConfigData(saved);
                        setCompanyConfigData((prev) => ({ ...(prev || {}), ...saved }));
                        applyBranchFields(saved);
                        persistCustomConfig({ ...(companyConfigData || {}), ...saved });
                    }
                    return;
                }
                ToastMsg(
                    "error",
                    isBranchManager
                        ? "Your employee record has no branch assigned"
                        : "Select a branch to save configuration"
                );
                return;
            }

            const payload = {
                ...pickFormFields(formData, BRANCH_SCOPED_FORM_FIELDS),
                id: branchConfigData?.id,
                branch_id: Number(selectedBranchId),
            };
            const response = await update(CUSTOM_CONFIGURATION, payload);
            if (response.status === "success") {
                ToastMsg("success", "Branch configuration saved successfully");
                const refreshed = await fetch(CUSTOM_CONFIGURATION, { branch: selectedBranchId });
                if (refreshed.status === "success" && refreshed.data?.[0]) {
                    setBranchConfigData(refreshed.data[0]);
                }
            }
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleOrgAdminBranchSelectionChange = (enabled) => {
        handleInputChange("org_admin_branch_selection", enabled);
        setBranchSelectionNotice(enabled ? "enabled" : "disabled");
    };

    const branchSelectionNoticeCopy =
        branchSelectionNotice === "enabled"
            ? {
                title: "Sign in again to see changes",
                description:
                    "Org admin branch selection is enabled. Please log out and log in again to see the branch picker and updated navigation.",
            }
            : {
                title: "Sign in again to see changes",
                description:
                    "Org admin branch selection is disabled. Please log out and log in again to land directly in the full app without the branch picker.",
    };

    const updateSlab = (index, key, value) => {
        setFormData((prev) => ({
            ...prev,
            late_fine_slabs: prev.late_fine_slabs.map((slab, i) =>
                i === index ? { ...slab, [key]: value } : slab
            ),
        }));
    };

    const addSlab = () => {
        setFormData((prev) => ({
            ...prev,
            late_fine_slabs: [
                ...prev.late_fine_slabs,
                { min_minutes: 1, max_minutes: 60, mode: "fine", fine: 0, deduction: 0 },
            ],
        }));
    };

    const removeSlab = (index) => {
        setFormData((prev) => ({
            ...prev,
            late_fine_slabs: prev.late_fine_slabs.filter((_, i) => i !== index),
        }));
    };

    useEffect(() => {
        if (!branchSelectionNotice) return undefined;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [branchSelectionNotice]);

    useEffect(() => {
        initializeConfiguration();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const reloadBranches = async () => {
            const defaultBranchId = await loadBranches();
            if (defaultBranchId && (BRANCH_SCOPED_SECTIONS.has(activeSection) || isBranchManager)) {
                await fetchBranchConfig(defaultBranchId);
            }
        };
        const onBranchScopeChange = () => {
            setBranchScopeTick((n) => n + 1);
            reloadBranches();
        };
        window.addEventListener(SESSION_STORAGE_HYDRATED_EVENT, reloadBranches);
        window.addEventListener(BRANCH_FILTER_CHANGED_EVENT, onBranchScopeChange);
        return () => {
            window.removeEventListener(SESSION_STORAGE_HYDRATED_EVENT, reloadBranches);
            window.removeEventListener(BRANCH_FILTER_CHANGED_EVENT, onBranchScopeChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSection]);

    useEffect(() => {
        if (!isOrgAdmin && activeSection === "features") {
            setActiveSection("overview");
        }
    }, [isOrgAdmin, activeSection]);

    useEffect(() => {
        if (activeSection === "features" && companyConfigData) {
            applyCompanyFields(companyConfigData);
        } else if (BRANCH_SCOPED_SECTIONS.has(activeSection)) {
            if (branchConfigData && selectedBranchId) {
                applyBranchFields(branchConfigData);
            } else if (companyConfigData) {
                applyBranchFields(companyConfigData);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSection]);

    const overviewCards = [
        { label: "Payroll processing", value: formData.zero_payroll ? "Zero attendance allowed" : "Attendance required", section: "payroll" },
        { label: "Late penalties", value: formData.late_penalty_enabled ? "Enabled" : "Disabled", section: "attendance" },
        { label: "Overtime pay", value: formData.overtime_enabled ? "Enabled" : "Disabled", section: "payroll" },
        {
            label: "Overtime mode",
            value: !formData.overtime_enabled
                ? "Off"
                : formData.overtime_approval_required
                    ? "Approval required"
                    : "Auto from attendance",
            section: "payroll",
        },
        { label: "TDS deduction", value: formData.tds_enabled ? "Enabled" : "Disabled", section: "tax" },
        { label: "Branch management", value: formData.is_branch_enabled ? "Enabled" : "Disabled", section: "features" },
        ...(isOrgAdmin ? [{
            label: "Org admin branch selection",
            value: formData.org_admin_branch_selection ? "Enabled" : "Disabled",
            section: "features",
        }] : []),
        { label: "Grace before late", value: `${formData.grace_times} min`, section: "attendance" },
        {
            label: "Permission on single punch",
            value: formData.block_permission_on_single_punch ? "Blocked" : "Allowed",
            section: "attendance",
        },
        {
            label: "Leave during probation",
            value: formData.block_leave_in_probation ? "Blocked" : "Allowed",
            section: "attendance",
        },
        {
            label: "Salary & payslip lock",
            value: formData.lock_employee_salary_payslip ? "Locked" : "Unlocked",
            section: "payroll",
        },
        {
            label: "Birthday alerts",
            value: formData.birthday_alerts_enabled ? "Enabled" : "Disabled",
            section: "features",
        },
    ];

    const visibleOverviewCards = isOrgAdmin
        ? overviewCards
        : overviewCards.filter((item) => item.section !== "features");

    const renderOverview = () => (
        <div className="space-y-6">
            <SectionHeader
                title="Configuration Overview"
                description={isBranchManager
                    ? "Payroll, attendance, and tax rules for your branch. Use the sections on the left to edit settings."
                    : "Company-wide payroll, attendance, and feature toggles. Use the sections on the left to edit settings, or jump to operational setup screens below."}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {visibleOverviewCards.map((item) => (
                    <button
                        key={item.label}
                        type="button"
                        onClick={() => setActiveSection(item.section)}
                        className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 text-left hover:border-sidebar-primary/40 hover:bg-sidebar/5 transition-colors"
                    >
                        <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{item.label}</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">{item.value}</p>
                    </button>
                            ))}
                        </div>
            <SettingCard icon={Cog} title="Quick edit">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                    Select a section from the sidebar to update payroll, attendance, tax, or feature flags. Payroll and attendance rules save for the selected branch. Feature flags such as NABH and branch management stay company-wide.
                </p>
            </SettingCard>
            </div>
        );

    const renderPayroll = () => (
        <div className="space-y-6">
            <SectionHeader
                title="Payroll & Deductions"
                description="Configure how payroll is calculated, statutory deductions, and pay adjustments."
            />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <SettingCard icon={Calculator} title="Provident Fund">
                        <div className="space-y-4">
                            <NumberField
                                label="Basic Salary Limit"
                                prefix="₹"
                                value={formData.pf_limit}
                            onChange={(e) => handleInputChange("pf_limit", parseInt(e.target.value, 10) || 0)}
                                hint="Minimum basic salary for PF eligibility"
                            />
                            <NumberField
                                label="Cap Amount"
                                prefix="₹"
                                value={formData.cap_amount}
                            onChange={(e) => handleInputChange("cap_amount", parseInt(e.target.value, 10) || 0)}
                                hint="Maximum PF contribution amount"
                            />
                            <ToggleRow
                            label="Statutory deductions (PF, ESIC, etc.)"
                            description="Enable tax and statutory deductions for employees"
                                checked={formData.is_deduction}
                            onChange={(v) => handleInputChange("is_deduction", v)}
                            />
                        </div>
                    </SettingCard>

                <SettingCard icon={Percent} title="Salary & LOP Base">
                        <div className="space-y-1">
                            <ToggleRow
                            label="Deduction base: CTC"
                            description="Calculate component deductions from CTC"
                            checked={formData.salary_deduction === "ctc"}
                            onChange={(v) => handleInputChange("salary_deduction", v ? "ctc" : "gross")}
                        />
                        <ToggleRow
                            label="Deduction base: Gross"
                            description="Calculate component deductions from gross salary"
                            checked={formData.salary_deduction === "gross"}
                            onChange={(v) => handleInputChange("salary_deduction", v ? "gross" : "ctc")}
                        />
                        <div className="border-t border-gray-100 dark:border-gray-700 my-2" />
                        <ToggleRow
                            label="LOP from gross salary"
                            description="Loss-of-pay deducted from gross"
                            checked={formData.lop_deduction === "gross_salary"}
                            onChange={(v) => handleInputChange("lop_deduction", v ? "gross_salary" : "net_salary")}
                            />
                            <ToggleRow
                            label="LOP from net salary"
                            description="Loss-of-pay deducted from net"
                            checked={formData.lop_deduction === "net_salary"}
                            onChange={(v) => handleInputChange("lop_deduction", v ? "net_salary" : "gross_salary")}
                            />
                        </div>
                    </SettingCard>

                <SettingCard icon={Wallet} title="Payroll Processing" className="xl:col-span-2">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-1">
                        <ToggleRow
                            label="Run payroll with zero attendance"
                            description="Allow payroll even when attendance data is missing"
                            checked={formData.zero_payroll}
                            onChange={(v) => handleInputChange("zero_payroll", v)}
                        />
                        <ToggleRow
                            label="Deduct taxes for contract employees"
                            description="Apply tax deductions to contract staff"
                            checked={formData.deduct_for_contract_emp}
                            onChange={(v) => handleInputChange("deduct_for_contract_emp", v)}
                        />
                            <ToggleRow
                            label="Calculate overtime pay"
                            description="Pay for hours worked beyond the shift"
                            checked={formData.overtime_enabled}
                            onChange={(v) => handleInputChange("overtime_enabled", v)}
                            />
                            {formData.overtime_enabled && (
                                <ToggleRow
                                    label="Auto-calculate overtime from attendance"
                                    description="When enabled, payroll uses detected attendance overtime directly and hides Overtime Request and Approval Inbox"
                                    checked={!formData.overtime_approval_required}
                                    onChange={(v) => handleInputChange("overtime_approval_required", !v)}
                                />
                            )}
                            <ToggleRow
                            label="Apply short-duty deduction"
                            description="Deduct for missing-punch days during payroll"
                            checked={formData.short_duty_deduction_enabled}
                            onChange={(v) => handleInputChange("short_duty_deduction_enabled", v)}
                            />
                            <ToggleRow
                            label="Lock employee salary & payslips"
                            description="During payroll, hide salary and payslip details on My Profile, and block overtime, allowance and deduction changes until payroll is complete"
                            checked={formData.lock_employee_salary_payslip}
                            onChange={(v) => handleInputChange("lock_employee_salary_payslip", v)}
                        />
                            <ToggleRow
                            label="Salary corrections in next payroll cycle"
                            description="When attendance is corrected after payslip is published, queue the salary impact for the next payroll run"
                            checked={formData.salary_correction_next_cycle}
                            onChange={(v) => handleInputChange("salary_correction_next_cycle", v)}
                        />
                            <ToggleRow
                            label="One regular payslip per employee per month"
                            description="Block accidental duplicate regular payroll runs for the same calendar payroll month"
                            checked={formData.enforce_one_payslip_per_month}
                            onChange={(v) => handleInputChange("enforce_one_payslip_per_month", v)}
                        />
                            <ToggleRow
                            label="Allow supplement payroll re-run"
                            description="Permit a second payroll run marked as supplement (e.g. mid-month increment or bonus) after the regular run"
                            checked={formData.allow_supplement_payroll}
                            onChange={(v) => handleInputChange("allow_supplement_payroll", v)}
                        />
                            <ToggleRow
                            label="Freeze payroll rules to the payroll month"
                            description="Pay a past month on the rules that were in force that month. Changing the PF limit, cap amount, deduction base, LOP base, overtime, short-duty or TDS settings above will not change what a re-run of an earlier month pays"
                            checked={formData.freeze_payroll_config}
                            onChange={(v) => handleInputChange("freeze_payroll_config", v)}
                        />
                    </div>
                    {(formData.overtime_enabled || formData.short_duty_deduction_enabled) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                            {formData.overtime_enabled && (
                                <>
                                    <NumberField
                                        label="Overtime rate multiplier"
                                        min="1"
                                        step="0.5"
                                        prefix="×"
                                        value={formData.overtime_multiplier}
                                        onChange={(e) => handleInputChange("overtime_multiplier", parseFloat(e.target.value) || 0)}
                                        hint="2 = statutory double rate"
                                    />
                                    <SelectField
                                        label="Overtime rounding"
                                        value={formData.overtime_rounding_minutes}
                                        onChange={(e) => handleInputChange("overtime_rounding_minutes", parseInt(e.target.value, 10))}
                                        options={OVERTIME_ROUNDING_OPTIONS}
                                        hint="Round extra hours before payroll calculation"
                                    />
                                </>
                            )}
                            {formData.short_duty_deduction_enabled && (
                                <>
                                    <NumberField
                                        label="Default short-duty deduction (days)"
                                        min="0"
                                        max="1"
                                        step="0.5"
                                        value={formData.short_duty_default_fraction}
                                        onChange={(e) => handleInputChange("short_duty_default_fraction", parseFloat(e.target.value) || 0)}
                                        hint="0.5 = half day, 1 = full day"
                                    />
                                    <NumberField
                                        label="Default fine amount"
                                        min="0"
                                        step="50"
                                        prefix="₹"
                                        value={formData.short_duty_default_fine}
                                        onChange={(e) => handleInputChange("short_duty_default_fine", parseFloat(e.target.value) || 0)}
                                        hint="Pre-filled when switching a day to fine mode"
                                    />
                                </>
                            )}
                        </div>
                    )}
                </SettingCard>
            </div>
        </div>
    );

    const renderAttendance = () => (
        <div className="space-y-6">
            <SectionHeader
                title="Attendance & Time"
                description="Grace periods, late penalties, and punch behaviour for employees."
            />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <SettingCard icon={Clock} title="Grace & Late Arrival">
                    <div className="space-y-4">
                        <SelectField
                            label="Check-in grace time"
                            value={formData.grace_times}
                            onChange={(e) => handleInputChange("grace_times", parseInt(e.target.value, 10))}
                            options={GRACE_TIME_OPTIONS}
                            hint="Allowance before marking an employee late"
                        />
                        <NumberField
                            label="Daily grace limit"
                            min="0"
                            value={formData.daily_grace_limit}
                            onChange={(e) => handleInputChange("daily_grace_limit", parseInt(e.target.value, 10) || 0)}
                            hint="Late arrivals allowed per day before penalty (0 = not enforced)"
                        />
                        <NumberField
                            label="Monthly grace limit"
                            min="0"
                            value={formData.monthly_grace_limit}
                            onChange={(e) => handleInputChange("monthly_grace_limit", parseInt(e.target.value, 10) || 0)}
                            hint="Late arrivals allowed per month before penalty (0 = not enforced)"
                        />
                    </div>
                </SettingCard>

                <SettingCard icon={FileText} title="Punch & Attendance Rules">
                    <ToggleRow
                        label="Biometric punch only IN/OUT"
                        description="Restrict biometric to check-in and check-out only"
                        checked={formData.biometric_punch_only_inout}
                        onChange={(v) => handleInputChange("biometric_punch_only_inout", v)}
                    />
                    <ToggleRow
                        label="Disable mobile clock-in"
                        description="Prevent employees from punching in via the mobile app"
                        checked={formData.is_disabled_clockin}
                        onChange={(v) => handleInputChange("is_disabled_clockin", v)}
                    />
                    <div className="border-t border-gray-100 dark:border-gray-700 my-2" />
                    <ToggleRow
                        label="Block permission on incomplete punch days"
                        description="When enabled, employees cannot raise permission requests on a day with only check-in (single punch / missing check-out), including the same day they punched in without checking out"
                        checked={formData.block_permission_on_single_punch}
                        onChange={(v) => handleInputChange("block_permission_on_single_punch", v)}
                    />
                    <ToggleRow
                        label="Block leave during probation"
                        description="When enabled, employees marked as probation period cannot submit leave requests. This applies across all branches. When disabled, probation employees can apply for leave"
                        checked={formData.block_leave_in_probation}
                        onChange={(v) => handleInputChange("block_leave_in_probation", v)}
                    />
                    {formData.overtime_enabled && formData.overtime_approval_required && (
                        <NumberField
                            label="Minimum overtime to raise a request"
                            min="0"
                            step="5"
                            suffix="min"
                            value={formData.overtime_request_min_minutes}
                            onChange={(e) => handleInputChange("overtime_request_min_minutes", parseInt(e.target.value, 10) || 0)}
                            hint="Employees can submit an OT request only when detected overtime meets or exceeds this threshold. 0 = any overtime."
                        />
                    )}
                    </SettingCard>

                <SettingCard icon={Percent} title="Late Penalty Rules" className="xl:col-span-2">
                            <ToggleRow
                        label="Auto-apply late penalties"
                        description="After grace limits are exceeded, apply fine or LOP from the slabs below"
                                checked={formData.late_penalty_enabled}
                        onChange={(v) => handleInputChange("late_penalty_enabled", v)}
                            />
                            {formData.late_penalty_enabled && (
                        <div className="space-y-3 pt-3 mt-3 border-t border-gray-100 dark:border-gray-700">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                Configure threshold slabs by minutes late (after grace time). Use Fine for a flat amount or Deduction for day fraction.
                                    </p>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
                                                <tr>
                                                    <th className="px-3 py-2">From (min)</th>
                                                    <th className="px-3 py-2">To (min)</th>
                                                    <th className="px-3 py-2">Mode</th>
                                                    <th className="px-3 py-2">Fine ₹</th>
                                                    <th className="px-3 py-2">Deduction (days)</th>
                                            <th className="px-3 py-2" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.late_fine_slabs.map((slab, index) => (
                                                    <tr key={index} className="border-t border-gray-100 dark:border-gray-700">
                                                        <td className="px-3 py-2">
                                                            <input type="number" min="0" value={slab.min_minutes}
                                                        onChange={(e) => updateSlab(index, "min_minutes", parseInt(e.target.value, 10) || 0)}
                                                                className="w-20 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1" />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input type="number" min="0" value={slab.max_minutes}
                                                        onChange={(e) => updateSlab(index, "max_minutes", parseInt(e.target.value, 10) || 0)}
                                                                className="w-20 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1" />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <CompactSelect
                                                                value={slab.mode}
                                                                onChange={(mode) => updateSlab(index, "mode", mode)}
                                                                options={PENALTY_MODE_OPTIONS}
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input type="number" min="0" value={slab.fine}
                                                        onChange={(e) => updateSlab(index, "fine", parseFloat(e.target.value) || 0)}
                                                                className="w-24 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1" />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <input type="number" min="0" max="1" step="0.25" value={slab.deduction}
                                                        onChange={(e) => updateSlab(index, "deduction", parseFloat(e.target.value) || 0)}
                                                                className="w-24 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1" />
                                                        </td>
                                                        <td className="px-3 py-2">
                                                            <button type="button" onClick={() => removeSlab(index)}
                                                                className="text-xs text-rose-600 hover:underline">Remove</button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <Button type="button" variant="outline" size="sm" onClick={addSlab}>
                                        Add slab
                                    </Button>
                                </div>
                            )}
                </SettingCard>
            </div>
                        </div>
    );

    const renderTax = () => (
        <div className="space-y-6">
            <SectionHeader
                title="Tax & Compliance"
                description="Income tax (TDS) master switch and default regime for new employees."
            />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <SettingCard icon={Receipt} title="TDS (Income Tax)">
                    <ToggleRow
                        label="Enable TDS deduction in payroll"
                        description="When off, payroll deducts no income tax regardless of declarations"
                        checked={formData.tds_enabled}
                        onChange={(v) => handleInputChange("tds_enabled", v)}
                    />
                    <SelectField
                        label="Default tax regime for new employees"
                        value={formData.tds_default_regime}
                        onChange={(e) => handleInputChange("tds_default_regime", e.target.value)}
                        options={TDS_REGIME_OPTIONS}
                        hint="Used when an employee has not opted into a regime"
                    />
                </SettingCard>
                <SettingCard icon={Calculator} title="TDS rate slabs">
                    <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                        Tax rate grids are configured per financial year on a separate screen. Update slabs whenever budget rates change.
                    </p>
                    <LinkCard
                        icon={Calculator}
                        title="Open TDS Slab Configuration"
                        description="Manage FY-wise tax slabs, surcharge, and rebates"
                        href="/payroll/tdsconfiguration"
                    />
                    </SettingCard>
            </div>
        </div>
    );

    const renderFeatures = () => (
        <div className="space-y-6">
            <SectionHeader
                title="Features & Access"
                description="Enable or disable optional modules and employee-facing capabilities."
            />
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <SettingCard icon={Sparkles} title="Module Features">
                            <ToggleRow
                        label="Branch management"
                        description="Enable multi-branch setup, branch admin roles, and branch transfer"
                        checked={formData.is_branch_enabled}
                        onChange={(v) => handleInputChange("is_branch_enabled", v)}
                    />
                    {isOrgAdmin && (
                        <ToggleRow
                            label="Org admin branch selection"
                            description="When enabled, org admins with multiple branches see the branch picker and Dashboard Overview after login. When disabled, org admins land directly in the full app with all branch and employee data."
                            configKey="org_admin_branch_selection"
                            checked={formData.org_admin_branch_selection}
                            onChange={handleOrgAdminBranchSelectionChange}
                        />
                    )}
                    <ToggleRow
                        label="NABH / Training module"
                        description="Show the Training module in navigation for compliance tracking"
                        checked={formData.is_nabh}
                        onChange={(v) => handleInputChange("is_nabh", v)}
                            />
                            <ToggleRow
                        label="Allow deletion of issued documents"
                        description="Permit users to delete documents after they have been issued"
                        checked={formData.modify_issued_documents}
                        onChange={(v) => handleInputChange("modify_issued_documents", v)}
                    />
                </SettingCard>

                <SettingCard icon={CalendarClock} title="Roster & Shifts">
                    <ToggleRow
                        label="Roster & shift management"
                        description="Show shift controls on the organization hierarchy, the duty roster grid, and shift-change requests. Off by default: these screens can re-stamp an attendance day that payroll has already read, so switch it on once your roster owners are ready."
                        checked={formData.roster_shift_management_enabled}
                        onChange={(v) => handleInputChange("roster_shift_management_enabled", v)}
                    />
                    <NumberField
                        label="Unused shift window (days)"
                        min="14"
                        max="1095"
                        value={formData.unused_shift_period_days}
                        onChange={(e) => handleInputChange("unused_shift_period_days", parseInt(e.target.value, 10) || 0)}
                        hint="A shift is flagged Unused when nothing has rostered, overridden, worked or been assigned to it for this many days. Longer suits a quarterly rotation; shorter suits weekly rostering."
                    />
                    <ToggleRow
                        label="Weekly unused shift digest"
                        description="Send roster owners a Monday summary of shifts nobody is using. The highlighting on the Shift Configuration screen works either way — this only controls the message."
                        checked={formData.unused_shift_alert_enabled}
                        onChange={(v) => handleInputChange("unused_shift_alert_enabled", v)}
                    />
                </SettingCard>

                <SettingCard icon={Cake} title="Dashboard & Alerts">
                    <ToggleRow
                        label="Birthday alerts"
                        description="Notify HR admins on birthdays today, and show a personal celebration (confetti + message) on the employee's home screen"
                        checked={formData.birthday_alerts_enabled}
                        onChange={(v) => handleInputChange("birthday_alerts_enabled", v)}
                    />
                </SettingCard>

                <SettingCard icon={MapPin} title="Field & Mobile">
                    <ToggleRow
                        label="Salesperson location tracking"
                        description="Track field staff location during working hours"
                        checked={formData.loc_track_ctl}
                        onChange={(v) => handleInputChange("loc_track_ctl", v)}
                    />
                </SettingCard>
            </div>
        </div>
    );

    const renderSetup = () => (
        <div className="space-y-8">
            <SectionHeader
                title="Operational Setup"
                description="Master data and structural configuration managed on dedicated screens."
            />
            {OPERATIONAL_LINKS.map((group) => (
                <div key={group.group}>
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                        {group.group}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {group.items.map((item) => (
                            <LinkCard key={item.href} {...item} />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );

    const renderSectionContent = () => {
        switch (activeSection) {
            case "payroll": return renderPayroll();
            case "attendance": return renderAttendance();
            case "tax": return renderTax();
            case "features": return renderFeatures();
            case "setup": return renderSetup();
            default: return renderOverview();
        }
    };

    if (loading) {
        return (
            <div className="animate-pulse space-y-6">
                <div className="space-y-2">
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/3" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/2" />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
                    <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-2xl" />
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-2xl" />
                        ))}
                    </div>
                            </div>
                        </div>
        );
    }

    const showSaveBar =
        activeSection !== "setup" &&
        activeSection !== "overview" &&
        (activeSection === "features" || selectedBranchId || !branches.length);

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                    <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-sidebar/10 text-sidebar-primary grid place-items-center">
                        <Cog className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Configuration</h1>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {isBranchManager || isBranchScopedSection
                                ? "Branch-specific payroll, attendance, tax, and operational settings."
                                : "Company-wide feature toggles and access settings."}
                        </p>
                    </div>
                </div>
                {showBranchPicker && (
                    <ConfigBranchSelect
                        branches={branches}
                        value={selectedBranchId}
                        onChange={handleBranchChange}
                    />
                )}
                {isBranchManager && (
                    <div className="flex items-center gap-2 px-3 h-10 rounded-full border border-sidebar-primary/20 bg-sidebar-primary/15 text-sidebar-primary text-sm font-semibold min-w-[180px]">
                        <Building2 className="h-4 w-4 shrink-0" />
                        <span className="truncate">{ownBranchName || (ownBranchId ? `Branch ${ownBranchId}` : "No branch assigned")}</span>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[220px_1fr]">
                <nav className="flex flex-row gap-1 overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-2 lg:flex-col lg:overflow-visible">
                    {visibleSections.map((section) => {
                        const active = activeSection === section.id;
                        return (
                            <button
                                key={section.id}
                                type="button"
                                onClick={() => setActiveSection(section.id)}
                                className={cn(
                                    "flex items-center gap-2.5 whitespace-nowrap rounded-xl px-3 py-2.5 text-sm font-medium transition-colors lg:w-full",
                                    active
                                        ? "bg-sidebar/10 text-sidebar-primary"
                                        : "text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/60"
                                )}
                            >
                                <section.icon className="h-4 w-4 shrink-0" />
                                <span className="flex-1 text-left">{section.label}</span>
                            </button>
                        );
                    })}
                </nav>

                <div className="min-w-0">
                    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/40 p-5 lg:p-6">
                        {activeMeta.description && activeSection !== "overview" && activeSection !== "setup" && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 pb-4 border-b border-gray-200 dark:border-gray-700">
                                {activeMeta.description}
                            </p>
                        )}
                        {renderSectionContent()}
                    </div>

                    {showSaveBar && (
                        <div className="flex justify-end pt-4">
                <Button
                    variant="brand"
                    onClick={handleSave}
                    disabled={saving}
                    className="font-medium flex items-center gap-2"
                >
                    <Save className="h-4 w-4" />
                                {saving ? "Saving..." : "Save Configuration"}
                </Button>
                        </div>
                    )}
                </div>
            </div>

            {branchSelectionNotice && createPortal(
                <div
                    className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                    onClick={() => setBranchSelectionNotice(null)}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="branch-selection-notice-title"
                        className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-gray-200 dark:border-gray-700"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="flex items-center gap-3 mb-4">
                            <span className="w-11 h-11 rounded-xl bg-sidebar/10 text-sidebar-primary grid place-items-center shrink-0">
                                <Info className="h-5 w-5" />
                            </span>
                            <h3
                                id="branch-selection-notice-title"
                                className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                            >
                                {branchSelectionNoticeCopy.title}
                            </h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                            {branchSelectionNoticeCopy.description}
                        </p>
                        <div className="flex gap-3 justify-end">
                            <Button
                                variant="outline"
                                onClick={() => setBranchSelectionNotice(null)}
                                className="border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 px-6"
                            >
                                Close
                            </Button>
                            <Button
                                variant="brand"
                                onClick={() => setBranchSelectionNotice(null)}
                                className="px-6"
                            >
                                OK
                            </Button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default SettingsConfiguration;
